import Logo from "../../components/Logo";
import StepOne from "./Steps/StepOne.js";
import StepTwo from "./Steps/StepTwo.js";
import Success from "./Steps/Success.js";
import ResultStep from "./Steps/ResultStep.js";
import { useState, useRef, useCallback, useEffect } from "react";
import { useSystem } from "../../hooks/use-system";
import LanguageSelector from "../../components/LanguageSelector";
import { defaultLanguage } from "../../constants/languages";
import { Progress } from "../../components/UI/progress";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { isMobile } from 'react-device-detect';

export default function Signup() {
  const { config, language } = useSystem();
  const [registerStep, setRegisterStep] = useState(0);
  const [isLandscape, setIsLandscape] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  const stepOneRef = useRef(null);
  const stepTwoRef = useRef(null);
  const nodeRef = registerStep === 0 ? stepOneRef : stepTwoRef;
  const [verificationRequest, setVerificationRequest] = useState(null);
  const [carrierCode, setCarrierCode] = useState(null);
  const [shortCode, setShortCode] = useState(null);
  const [keyword, setKeyword] = useState(null);

  // Detect device type and orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      const isMobileSize = window.innerWidth < 1024;
      const isLandscapeMode = window.innerWidth > window.innerHeight;
      setIsLandscape(isLandscapeMode && isMobileSize);
      setIsMobileDevice(isMobile || isMobileSize);
    };

    // Initial check
    handleOrientationChange();

    // Listen for orientation changes
    window.addEventListener('resize', handleOrientationChange);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  const handleVerificationRequest = useCallback((value) => {
    setVerificationRequest(value);
  }, []);

  const handleCarrierCode = useCallback((value) => {
    setCarrierCode(value);
  }, []);

  const handleSmsInfo = useCallback((shortCodeValue, keywordValue) => {
    setShortCode(shortCodeValue);
    setKeyword(keywordValue);
  }, []);

  const needsSuccessStep = () => {
    return config.flow === "PIN_FLOW";
  };

  const isSmsBased = () => {
    return [
      "LOOKUP_2_SMS",
      "CLICK_2_SMS", 
      "CLICK_2_SMS_REDIRECT", 
      "PREMIUM_CLICK_2_SMS", 
      "MSISDN_REDIRECT", 
      "INIT_MSISDN_REDIRECT"
    ].includes(config.flow);
  };

  const renderCurrentStep = () => {
    if (registerStep === 0) {
      if (config.flow === "CLICK_2_SMS") {
        return (
          <ResultStep
            config={config}
            language={language}
            shortCode={shortCode}
            keyword={keyword}
            setStep={setRegisterStep}
            isLandscape={isLandscape}
          />
        );
      }
      return (
        <StepOne
          setStep={setRegisterStep}
          config={config}
          language={language}
          handleVerificationRequest={handleVerificationRequest}
          handleCarrierCode={handleCarrierCode}
          handleSmsInfo={handleSmsInfo}
          isLandscape={isLandscape}
        />
      );
    } else if (registerStep === 1) {
      if (config.flow === "PIN_FLOW") {
        return (
          <StepTwo
            setStep={setRegisterStep}
            config={config}
            language={language}
            verificationRequest={verificationRequest}
            carrierCode={carrierCode}
            isLandscape={isLandscape}
          />
        );
      } else if (isSmsBased()) {
        return (
          <ResultStep
            config={config}
            language={language}
            shortCode={shortCode}
            keyword={keyword}
            setStep={setRegisterStep}
            isLandscape={isLandscape}
          />
        );
      }
    }
    return null;
  };

  // Render landscape layout for mobile devices
  if (isLandscape && isMobileDevice) {
    return (
      <div className="w-full max-w-[100vw] overflow-x-hidden">
        {(registerStep === 2 && needsSuccessStep()) ? (
          <div className="signup min-h-screen bg-center bg-no-repeat bg-cover relative flex flex-col overflow-hidden w-full">
            <Success isLandscape={isLandscape} />
          </div>
        ) : (
          <div
            className="signup min-h-screen flex flex-col overflow-hidden max-w-full w-full"
            style={{
              backgroundColor: config.background_color,
              color: config.text_color,
              fontFamily: config.font_family,
            }}
          >
            {/* Full width form content in landscape mode */}
            <div className="w-full flex flex-col px-4 bg-white relative min-h-screen overflow-y-auto overflow-x-hidden">
              <div className="flex items-center justify-end w-full pt-2 pb-1">
                <LanguageSelector
                  supportedLanguages={config.supported_languages}
                />
              </div>
              
              <div className="w-full bg-white rounded shadow-sm border border-gray-200 p-1.5 mb-2 flex items-center mx-auto mt-1 h-auto min-h-[55px]">
                <div className="bg-gray-100 p-1.5 rounded-md mr-2 flex-shrink-0">
                  <img
                    src={config.logo_url || "/placeholder.svg"}
                    alt="Service icon"
                    className="w-5 h-5 object-contain"
                  />
                </div>
                <div className="overflow-hidden flex-1 flex flex-col justify-center py-0.5">
                  <div 
                    className="text-xs overflow-hidden text-left mb-0"
                    style={{ 
                      color: config.titles_color || "#000",
                      wordBreak: "normal",
                      hyphens: "manual",
                      overflowWrap: "normal",
                      lineHeight: "1.2",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: config.texts?.[language || defaultLanguage]?.[registerStep]?.header_text || "Header text should be here..."
                    }}
                  />
                </div>
              </div>
              
              <div className="w-full mx-auto flex flex-col mt-1">
                <h1 className="text-xl font-bold text-center mb-1"
                  style={{ color: config.titles_color || "#000" }}>
                  {config.texts?.[language || defaultLanguage]?.[registerStep]?.title || "Register now!"}
                </h1>
                <p className="text-sm text-center mb-2 font-medium"
                  style={{ color: config.text_color || "#000" }}>
                  {config.texts?.[language || defaultLanguage]?.[registerStep]?.sec_title || "Follow few steps to sign up."}
                </p>

                {config.flow !== "CLICK_2_SMS" && (
                  <div className="flex justify-center space-x-8 mb-3 w-full">
                    <div className="flex items-center">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white font-bold text-xs mr-2 flex-shrink-0">
                        1
                      </div>
                      <span className="font-semibold text-xs flex items-center whitespace-nowrap"
                        style={{ color: config.text_color || "#000" }}>
                        {config.texts?.[language || defaultLanguage]?.[registerStep]?.step_one_text || "Enter your phone number"}
                      </span>
                    </div>
                    
                    {(config.flow !== "PREMIUM_CLICK_2_AUTO_SMS") && (
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs mr-2 flex-shrink-0 ${
                          registerStep === 1 ? "bg-blue-600 text-white" : "bg-gray-300 text-gray-600"
                        }`}>
                          2
                        </div>
                        <span className={`text-xs font-semibold flex items-center whitespace-nowrap ${
                          registerStep === 1 ? "text-gray-900" : "text-gray-600"
                        }`}
                          style={registerStep === 1 ? { color: config.text_color || "#000" } : {}}>
                          {config.flow === "PIN_FLOW" 
                            ? (config.texts?.[language || defaultLanguage]?.[registerStep]?.step_two_text || "Verify your number") 
                            : (config.texts?.[language || defaultLanguage]?.[registerStep]?.step_two_text || "Send SMS")}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="w-full items-center justify-center">
                  <SwitchTransition mode="out-in">
                    <CSSTransition
                      key={registerStep}
                      classNames="fade"
                      nodeRef={nodeRef}
                      addEndListener={(done) => {
                        nodeRef.current.addEventListener(
                          "transitionend",
                          done,
                          false
                        );
                      }}
                      timeout={1000}
                    >
                      <div ref={nodeRef} className="w-full overflow-hidden flex justify-center">
                        {renderCurrentStep()}
                      </div>
                    </CSSTransition>
                  </SwitchTransition>
                </div>

                {/* Paragraph under button */}
                <div className="mt-1 text-center w-full flex-col mx-auto items-center justify-center">
                  <div
                    className="font-medium text-[9px] text-center leading-[12px] flex flex-col gap-2 w-full"
                    dangerouslySetInnerHTML={{
                      __html:
                        config.texts?.[language || defaultLanguage]?.[registerStep]
                          ?.paragraph_under_button || "",
                    }}
                  ></div>
                </div>

                <div className="flex-grow" />

                {/* Footer Links */}
                <div className="flex flex-col items-center">
                  {config.flow_icon && (
                    <div className="w-full my-1">
                      <img
                        src={config.flow_icon}
                        alt="Flow Icon"
                        className="mx-auto max-h-4"
                      />
                    </div>
                  )}
                  <div className="w-full flex flex-wrap justify-center text-xs text-center space-x-2">
                    <a href={config.terms_url} className="text-gray-700">
                      {config.translations?.[language || defaultLanguage]?.terms}
                    </a>

                    {config.policy_url && (
                      <>
                        <span className="text-light">|</span>
                        <a
                          href={config.policy_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: config.titles_color }}
                          className="text-gray-700"
                        >
                          {config.translations?.[language || defaultLanguage]?.policy}
                        </a>
                      </>
                    )}

                    {config.about_text && (
                      <>
                        <span className="text-light">|</span>
                        <a
                          href={config.about_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: config.titles_color }}
                          className="text-gray-700"
                        >
                          {config.translations?.[language || defaultLanguage]?.about}
                        </a>
                      </>
                    )}

                    {config.disclaimer_text && (
                      <>
                        <span className="text-light">|</span>
                        <a
                          href={config.disclaimer_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: config.titles_color }}
                          className="text-gray-700"
                        >
                          {config.translations?.[language || defaultLanguage]?.disclaimer}
                        </a>
                      </>
                    )}

                    {config.contact_us_text && (
                      <>
                        <span className="text-light">|</span>
                        <a
                          href={config.contact_us_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: config.titles_color }}
                          className="text-gray-700"
                        >
                          {config.translations?.[language || defaultLanguage]?.contact}
                        </a>
                      </>
                    )}
                  </div>
                </div>
                
                {config.bottom_text && (
                  <div id={'bottomText'} className="relative text-center text-xs text-gray-600 mb-2" dangerouslySetInnerHTML={{__html: config.translations?.[language || defaultLanguage]?.bottom_text}}></div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Regular portrait layout (existing code)
  return (
    <div className="w-full max-w-[100vw] overflow-x-hidden">
      {(registerStep === 2 && needsSuccessStep()) ? (
        <div className="signup min-h-screen bg-center bg-no-repeat bg-cover relative flex flex-col overflow-hidden w-full">
          <Success isLandscape={isLandscape} />
        </div>
      ) : (
        <div
          className="signup min-h-screen flex flex-col-reverse sm:flex-row overflow-hidden max-w-full w-full"
          style={{
            backgroundImage: `url(${config.background_image})`,
            backgroundColor: config.background_color,
            color: config.text_color,
            fontFamily: config.font_family,
          }}
        >
          <div className="md:w-1/2 bg-blue-500 justify-center items-center relative hidden sm:flex">
            <img
              src={config.featured_image}
              className="max-w-full h-auto w-[80%] max-h-screen object-contain"
              alt="Featured"
            />
          </div>
          <div className="md:w-1/2 w-full flex flex-col px-3 sm:px-4 md:px-6 lg:px-[40px] bg-white relative min-h-screen overflow-y-auto overflow-x-hidden">
            <div className="flex items-center justify-end w-full pt-3 sm:pt-4">
              <LanguageSelector
                supportedLanguages={config.supported_languages}
              />
            </div>
            
            <div className="w-full max-w-xs sm:max-w-sm md:max-w-md bg-white rounded shadow-sm border border-gray-200 p-1.5 sm:p-2 md:p-3 mb-3 sm:mb-4 flex items-center mx-auto mt-2 sm:mt-3 h-auto min-h-[60px] sm:min-h-[65px] md:min-h-[70px]">
              <div className="bg-gray-100 p-1.5 sm:p-2 rounded-md mr-2 flex-shrink-0">
                <img
                  src={config.logo_url || "/placeholder.svg"}
                  alt="Service icon"
                  className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 object-contain"
                />
              </div>
              <div className="overflow-hidden flex-1 flex flex-col justify-center py-0.5">
                <div 
                  className="text-xs sm:text-sm md:text-base overflow-hidden text-left mb-0"
                  style={{ 
                    color: config.titles_color || "#000",
                    wordBreak: "normal",
                    hyphens: "manual",
                    overflowWrap: "normal",
                    lineHeight: "1.2",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: config.texts?.[language || defaultLanguage]?.[registerStep]?.header_text || "Header text should be here..."
                  }}
                />
              </div>
            </div>

            <div className="w-full max-w-xs sm:max-w-sm md:max-w-md mx-auto flex flex-col">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-4xl font-bold text-center mb-1 sm:mb-2 md:mb-3"
                style={{ color: config.titles_color || "#000" }}>
                {config.texts?.[language || defaultLanguage]?.[registerStep]?.title || "Register now!"}
              </h1>
              <p className="text-base md:text-lg text-center mb-8 sm:mb-10 md:mb-12 lg:mb-14 font-medium"
                style={{ color: config.text_color || "#000" }}>
                {config.texts?.[language || defaultLanguage]?.[registerStep]?.sec_title || "Follow few steps to sign up."}
              </p>

              {config.flow !== "CLICK_2_SMS" && (
                <div className="space-y-3 sm:space-y-4 md:space-y-6 mb-4 sm:mb-6 md:mb-8 pl-2 relative w-full overflow-hidden">
                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-600 text-white font-bold text-lg mr-2 sm:mr-3 md:mr-4 flex-shrink-0">
                      1
                    </div>
                    <span className="font-semibold text-sm sm:text-base flex items-center"
                      style={{ color: config.text_color || "#000" }}>
                      {config.texts?.[language || defaultLanguage]?.[registerStep]?.step_one_text || "Enter your phone number"}
                    </span>
                  </div>
                  
                  {(config.flow !== "PREMIUM_CLICK_2_AUTO_SMS") && (
                    <div className="flex items-center">
                      <div className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full font-bold text-lg mr-2 sm:mr-3 md:mr-4 flex-shrink-0 ${
                        registerStep === 1 ? "bg-blue-600 text-white" : "bg-gray-300 text-gray-600"
                      }`}>
                        2
                      </div>
                      <span className={`text-sm sm:text-base font-semibold flex items-center ${
                        registerStep === 1 ? "text-gray-900" : "text-gray-600"
                      }`}
                        style={registerStep === 1 ? { color: config.text_color || "#000" } : {}}>
                        {config.flow === "PIN_FLOW" 
                          ? (config.texts?.[language || defaultLanguage]?.[registerStep]?.step_two_text || "Verify your number") 
                          : (config.texts?.[language || defaultLanguage]?.[registerStep]?.step_two_text || "Send SMS")}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="w-full items-center justify-center">
                <SwitchTransition mode="out-in">
                  <CSSTransition
                    key={registerStep}
                    classNames="fade"
                    nodeRef={nodeRef}
                    addEndListener={(done) => {
                      nodeRef.current.addEventListener(
                        "transitionend",
                        done,
                        false
                      );
                    }}
                    timeout={1000}
                  >
                    <div ref={nodeRef} className="w-full overflow-hidden">
                      {renderCurrentStep()}
                    </div>
                  </CSSTransition>
                </SwitchTransition>
              </div>
            </div>

            {/* Paragraph under button */}
            <div className="mt-1 text-center w-full flex-col mx-auto items-center justify-center max-w-xs sm:max-w-sm md:max-w-md">
              <div
                className="font-medium text-[9px] sm:text-[10px] text-center leading-[12px] sm:leading-[14px] flex flex-col gap-2 w-full"
                dangerouslySetInnerHTML={{
                  __html:
                    config.texts?.[language || defaultLanguage]?.[registerStep]
                      ?.paragraph_under_button || "",
                }}
              ></div>
            </div>

            <div className="flex-grow" />

            {/* Footer Links */}
            <div className="flex flex-col items-center">
              {config.flow_icon && (
                <div className="w-full my-1 sm:my-2">
                  <img
                    src={config.flow_icon}
                    alt="Flow Icon"
                    className="mx-auto max-h-6"
                  />
                </div>
              )}
              <div className="w-full flex flex-wrap justify-center text-xs sm:text-sm text-center space-x-3 sm:space-x-4">
                <a href={config.terms_url} className="text-gray-700">
                  {config.translations?.[language || defaultLanguage]?.terms}
                </a>

                {config.policy_url && (
                  <>
                    <span className="text-light">|</span>
                    <a
                      href={config.policy_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: config.titles_color }}
                      className="text-gray-700"
                    >
                      {config.translations?.[language || defaultLanguage]?.policy}
                    </a>
                  </>
                )}

                {config.about_text && (
                  <>
                    <span className="text-light">|</span>
                    <a
                      href={config.about_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: config.titles_color }}
                      className="text-gray-700"
                    >
                      {config.translations?.[language || defaultLanguage]?.about}
                    </a>
                  </>
                )}

                {config.disclaimer_text && (
                  <>
                    <span className="text-light">|</span>
                    <a
                      href={config.disclaimer_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: config.titles_color }}
                      className="text-gray-700"
                    >
                      {config.translations?.[language || defaultLanguage]?.disclaimer}
                    </a>
                  </>
                )}

                {config.contact_us_text && (
                  <>
                    <span className="text-light">|</span>
                    <a
                      href={config.contact_us_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: config.titles_color }}
                      className="text-gray-700"
                    >
                      {config.translations?.[language || defaultLanguage]?.contact}
                    </a>
                  </>
                )}
              </div>
            </div>
              
            {config.bottom_text && (
              <div id={'bottomText'} className="relative mt-2 text-center text-xs text-gray-600" dangerouslySetInnerHTML={{__html: config.translations?.[language || defaultLanguage]?.bottom_text}}></div>
            )}
          </div>

          {config.logo_url && config.logo_under_button && (
            <Logo
              logo_url={config.logo_url}
              logo_position={config.logo_position}
              logo_size={config.logo_size}
              classes="bottom"
            />
          )}
        </div>
      )}
    </div>
  );
}
